import { useEffect, useRef, useState } from "react";

import { testUploadImageRequest } from "../../../../http-request/testUploadRequest";
import { uploadImageRequest } from "../../../../http-request/uploadImageRequest";
import dummyImage from "../../../../assets/dummyPreview.png";
import classes from "./formImagePreviewAndUpload.module.css";

export const FormImagePreviewAndUpload = ({
  id,
  url,
  label = "📂 Browse Image",
  uploadType,
  extraParams = {},
  disabled,
  handleConfirm,
  register,
  setValue,
  validation = {},
  minWidth = 0,
  minHeight = 0,
}) => {
  const [imageSrc, setImageSrc] = useState(dummyImage);
  const [fileName, setFileName] = useState("");
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [isLoadError, setIsLoadError] = useState(false);

  const objectUrlRef = useRef(null);

  /* ---------- Derived flags ---------- */
  const hasImage = Boolean(url);

  const isTooSmall =
    dimensions.width > 0 &&
    (dimensions.width < minWidth || dimensions.height < minHeight);

  const isValidImage = hasImage && !isTooSmall && !isLoadError;

  const displaySrc = isLoadError ? dummyImage : imageSrc;

  /* ---------- Sync external URL (edit mode) ---------- */
  useEffect(() => {
    if (typeof url === "string") {
      setImageSrc(url);
      setFileName(url.split("/").pop());
      setSelectedFile(null);
      setIsChanged(false);
      setIsLoadError(false);
      setDimensions({ width: 0, height: 0 });
    } else {
      setImageSrc(dummyImage);
      setFileName("");
    }
  }, [url]);

  /* ---------- Cleanup object URL ---------- */
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  /* ---------- File Select ---------- */
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    const previewUrl = URL.createObjectURL(file);
    objectUrlRef.current = previewUrl;

    setImageSrc(previewUrl);
    setFileName(file.name);
    setSelectedFile(file);
    setIsChanged(true);
    setIsLoadError(false);
    setDimensions({ width: 0, height: 0 });

    const img = new Image();
    img.src = previewUrl;

    img.onload = () =>
      setDimensions({ width: img.width, height: img.height });

    img.onerror = () => {
      setIsLoadError(true);
      setImageSrc(dummyImage);
      setDimensions({ width: 0, height: 0 });
    };
  };

  /* ---------- Upload ---------- */
  const handleUpload = async () => {
    if (!selectedFile || isTooSmall || isLoadError) return;

    try {
      setIsUploading(true);

      const response =
        uploadType === "test"
          ? await testUploadImageRequest(selectedFile, extraParams)
          : await uploadImageRequest(selectedFile);

      const uploadedUrl =
        response?.fileUrl ||
        response?.data?.fileUrl ||
        response?.data?.url ||
        response?.url;

      if (typeof uploadedUrl === "string") {
        setImageSrc(uploadedUrl);
        setFileName(uploadedUrl.split("/").pop());
        setSelectedFile(null);
        setIsChanged(false);
        setIsLoadError(false);

        setValue?.(id, uploadedUrl, { shouldValidate: true });
        handleConfirm?.(id, uploadedUrl);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={classes.wrapper}>
      {/* Browse */}
      <label
        htmlFor={`upload-${id}`}
        className={`${classes.browseButton} ${
          isValidImage ? classes.browseValid : ""
        } ${isTooSmall || isLoadError ? classes.browseError : ""}`}
      >
        {fileName || label}
        <input
          id={`upload-${id}`}
          type="file"
          disabled={disabled}
          onChange={handleFileSelect}
          className={classes.fileInput}
        />
      </label>

      {/* RHF hidden input */}
      {register && (
        <input
          type="hidden"
          {...register(id, {
            ...validation,
            validate: () => {
              if (!url) return "Image is required";
              if (isLoadError) return "Image is corrupt or not supported";
              if (isTooSmall)
                return `Minimum size: ${minWidth}px × ${minHeight}px`;
              return true;
            },
          })}
        />
      )}

      {/* Preview */}
      <div
        className={`${classes.preview} ${
          isTooSmall || isLoadError ? classes.warnBorder : ""
        }`}
      >
        <img
          src={displaySrc}
          alt="Preview"
          className={classes.previewImg}
          onError={() => {
            setIsLoadError(true);
            setImageSrc(dummyImage);
          }}
        />
      </div>

      {/* Dimensions */}
      {dimensions.width > 0 && (
        <div
          className={`${classes.dimensionText} ${
            isTooSmall ? classes.dimensionError : classes.dimensionValid
          }`}
        >
          Dimensions: {dimensions.width}px × {dimensions.height}px
        </div>
      )}

      {/* Messages */}
      {!hasImage && <div className={classes.helperText}>No image selected</div>}

      {isLoadError && (
        <div className={classes.errorMessage}>
          Image is corrupt or not supported
        </div>
      )}

      {hasImage && isTooSmall && (
        <div className={classes.errorMessage}>
          Minimum size: {minWidth}px × {minHeight}px
        </div>
      )}

      {/* Confirm */}
      {isChanged && !isLoadError && !isTooSmall && (
        <button
          type="button"
          className={classes.confirmBtn}
          onClick={handleUpload}
          disabled={isUploading}
        >
          {isUploading ? "Uploading..." : "Confirm & Upload"}
        </button>
      )}
    </div>
  );
};
